import type {
  PaymentMethodSummaryResponseDto,
  SwitchToAnnualPreviewResponseDto,
  SwitchToAnnualResponseDto,
} from '@prospero/shared';
import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';
import type {
  ActivationCheckoutResponse,
  BillingDetails,
  BillingInterval,
  BillingPortalFlow,
  ChangePlanResponse,
  CheckoutResponse,
  CheckoutSessionStatus,
  DiscountCodeResponse,
  FullSubscriptionData,
  InvoiceItem,
  ISubscriptionService,
} from './subscription.service.interface';

@Injectable()
export class SubscriptionService implements ISubscriptionService {
  constructor(@inject(HttpService) private readonly http: IHttpService) {}

  async getFullSubscription(): Promise<FullSubscriptionData> {
    return this.http.get<FullSubscriptionData>('subscription/full');
  }

  async createCheckout(
    planTier: string,
    billingInterval: BillingInterval,
    couponId?: string,
  ): Promise<CheckoutResponse> {
    return this.http.post<CheckoutResponse>('subscription/checkout', {
      planTier,
      billingInterval,
      ...(couponId ? { couponId } : {}),
    });
  }

  async changePlan(
    billingInterval: BillingInterval,
    couponId?: string,
  ): Promise<ChangePlanResponse> {
    return this.http.post<ChangePlanResponse>('subscription/change-plan', {
      billingInterval,
      ...(couponId ? { couponId } : {}),
    });
  }

  async previewSwitchToAnnual(): Promise<SwitchToAnnualPreviewResponseDto> {
    return this.http.get<SwitchToAnnualPreviewResponseDto>('subscription/switch-to-annual/preview');
  }

  async switchToAnnual(prorationDate: number): Promise<SwitchToAnnualResponseDto> {
    return this.http.post<SwitchToAnnualResponseDto>('subscription/switch-to-annual', {
      prorationDate,
    });
  }

  async validateDiscountCode(code: string): Promise<DiscountCodeResponse> {
    return this.http.post<DiscountCodeResponse>('subscription/validate-discount-code', { code });
  }

  async createBillingPortalSession(flow?: BillingPortalFlow): Promise<{ url: string }> {
    return this.http.post<{ url: string }>('subscription/billing-portal', flow ? { flow } : {});
  }

  async createStripeHostedPaymentSession(
    billingInterval?: BillingInterval,
    couponId?: string,
  ): Promise<{ url: string }> {
    return this.http.post<{ url: string }>('subscription/stripe-hosted-payment', {
      ...(billingInterval ? { billingInterval } : {}),
      ...(couponId ? { couponId } : {}),
    });
  }

  async getInvoices(): Promise<InvoiceItem[]> {
    return this.http.get<InvoiceItem[]>('subscription/invoices');
  }

  async getPaymentMethodSummary(): Promise<PaymentMethodSummaryResponseDto> {
    return this.http.get<PaymentMethodSummaryResponseDto>('subscription/payment-method');
  }

  async cancelTrial(): Promise<void> {
    await this.http.post<void>('subscription/cancel-trial', {});
  }

  async setCancellation(
    cancelAtPeriodEnd: boolean,
  ): Promise<{ cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null }> {
    return this.http.post<{ cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null }>(
      'subscription/cancellation',
      { cancelAtPeriodEnd },
    );
  }

  async activateSubscription(
    billingInterval: BillingInterval,
    couponId?: string,
  ): Promise<ActivationCheckoutResponse> {
    return this.http.post<ActivationCheckoutResponse>('subscription/activate', {
      billingInterval,
      ...(couponId ? { couponId } : {}),
    });
  }

  async getCheckoutSessionStatus(sessionId: string): Promise<CheckoutSessionStatus> {
    return this.http.get<CheckoutSessionStatus>(
      `subscription/checkout-session/${sessionId}/status`,
    );
  }

  async getBillingDetails(): Promise<BillingDetails> {
    return this.http.get<BillingDetails>('subscription/billing-details');
  }

  async updateBillingEmail(email: string): Promise<{ billingEmail: string }> {
    return this.http.patch<{ billingEmail: string }>('subscription/billing-email', { email });
  }
}
